import {
  useGetPortfolioWeightsQuery,
  usePortfolioDryRunMutation,
  usePortfolioExecuteMutation,
  useGetSettingsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { PnLPill } from "@/components/domain/PnLPill";
import { fmtUsd, fmtTime } from "@/lib/format";
import { useMemo } from "react";
import { toast } from "sonner";

/**
 * V5 Portfolio: MPT target weights + drift + dry-run rebalance plan.
 * Mutating endpoints intentionally absent — runner still requires manual
 * approval through the existing Pending queue or Auto Settings toggle.
 */
export default function Portfolio() {
  const settings = useGetSettingsQuery();
  const weightsQ = useGetPortfolioWeightsQuery(200, { pollingInterval: 60000 });
  const [runDryRun, dryRun] = usePortfolioDryRunMutation();
  const [runExecute, execMut] = usePortfolioExecuteMutation();

  const enabled = !!settings.data?.mpt_enabled;
  const dryRunFlag = !!settings.data?.dry_run;
  const rows = weightsQ.data ?? [];

  const onExecute = async () => {
    const plan = dryRun.data;
    if (!plan?.rebalance_triggered) {
      toast.warning("No drift above threshold — nothing to execute.");
      return;
    }
    const summary = (plan.legs ?? [])
      .map((l) => `${l.direction} ${l.asset} $${l.size_usd.toFixed(2)}`)
      .join("\n");
    if (!confirm(
      `Execute MPT rebalance? This places REAL market orders.\n\n${summary}\n\n` +
      (dryRunFlag ? "(Currently in dry-run mode — orders will be simulated.)" : "(LIVE TRADING — orders will hit Binance.)")
    )) return;
    try {
      const r = await runExecute().unwrap();
      const okN = r.executed?.length ?? 0;
      const skN = r.skipped?.length ?? 0;
      const errN = r.errors?.length ?? 0;
      if (okN > 0) toast.success(`Executed ${okN} legs · skipped ${skN} · errors ${errN}`);
      else if (skN + errN > 0) toast.warning(`No legs executed · ${skN} skipped · ${errN} errors`);
      else toast.info(r.summary || "No drift above threshold.");
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Execute failed";
      toast.error(msg);
    }
  };

  // Group the latest set of weights (rows share `computed_at` for one MPT run).
  const latest = useMemo(() => {
    if (rows.length === 0) return null;
    const ts = rows[0].computed_at;
    const cohort = rows.filter((r) => r.computed_at === ts);
    return { ts, reason: cohort[0]?.reason ?? "", weights: cohort };
  }, [rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Portfolio (MPT max-Sharpe + threshold rebalance)</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => runDryRun()}
                disabled={!enabled || dryRun.isLoading}
                title={enabled ? "Compute target weights vs current allocation" : "Toggle mpt_enabled in Settings first"}
              >
                {dryRun.isLoading ? "Computing…" : "Run dry-run"}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={onExecute}
                disabled={!enabled || execMut.isLoading || !dryRun.data?.rebalance_triggered}
                title={
                  !dryRun.data
                    ? "Run dry-run first to see the plan"
                    : !dryRun.data.rebalance_triggered
                    ? "No drift above threshold"
                    : dryRunFlag ? "Simulate (dry-run mode)" : "Place real orders"
                }
              >
                {execMut.isLoading ? "Executing…"
                  : dryRunFlag ? "Execute (dry-run)" : "Execute LIVE"}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!enabled && (
            <p className="text-sm text-warn">
              <code>mpt_enabled = False</code>. The dry-run endpoint will refuse
              until you enable MPT in Settings → V5 → Portfolio. The runner
              never executes orders by itself; rebalance legs need your approval.
            </p>
          )}

          {dryRun.error ? (
            <p className="text-sm text-bad mt-2">
              {String((dryRun.error as { data?: { detail?: string } })?.data?.detail ?? "Dry-run failed.")}
            </p>
          ) : null}

          <div className="mt-3 space-y-2 text-xs text-muted leading-relaxed border-t border-border pt-3">
            <p>
              <strong className="text-fg">Robustness controls.</strong> MPT on noisy
              crypto returns is sensitive to corner solutions. Three optimizers are
              available via <code>mpt_objective</code>:{" "}
              <code className="text-fg">max_sharpe</code> (classic, most sensitive
              to noise), <code className="text-fg">min_volatility</code> (frontier
              point that ignores expected-return estimates), and{" "}
              <code className="text-fg">hrp</code> (Hierarchical Risk Parity —
              correlation-clustering allocation, no expected-return estimate at
              all; usually the most stable). The optimizer now uses 90 days of
              daily closes; weights are clipped to <code>mpt_max_per_asset_weight</code>;
              with under 4 assets the runner falls back to equal-weight.
              <code>mpt_use_exp_cov</code> swaps the flat-window Ledoit-Wolf
              shrinkage for an exponentially-weighted covariance (recent data
              dominates).
            </p>
            <p>
              <strong className="text-fg">Black-Litterman with V5 sentiment views.</strong>{" "}
              When <code>mpt_use_sentiment_views</code> is on, the optimizer reads the
              latest sentiment scores from the V5 sentiment subsystem and tilts
              expected returns by <code>mpt_view_strength × score</code> for each
              asset with <code>|score| ≥ 0.1</code>. Confidences scale inversely with
              <code>n_items</code> per score so a one-headline view doesn't dominate
              a five-headline one.
            </p>
            <p>
              <strong className="text-fg">Threshold rebalance.</strong> Plans only fire
              when an asset's drift exceeds <code>mpt_rebalance_threshold_pct</code>.
              Each plan is capped at <code>mpt_max_rebalance_trades_per_run</code> legs;
              remaining drift is queued for the next check.
            </p>
          </div>

          {execMut.data ? (
            <Alert tone={execMut.data.errors?.length ? "warn" : "info"} className="mt-2">
              <div className="text-sm font-medium">
                {execMut.data.summary}
                {execMut.data.dry_run ? <Badge tone="muted" className="ml-2">dry-run</Badge> : <Badge tone="warn" className="ml-2">LIVE</Badge>}
              </div>
              {execMut.data.executed?.length ? (
                <div className="mt-1 text-xs">
                  <strong className="text-good">Executed:</strong>{" "}
                  {execMut.data.executed.map((l, i) =>
                    <span key={i} className="mr-2">{l.direction} {l.asset} ${l.executed_usd?.toFixed(2)}{l.order_id ? ` (${l.order_id})` : ""}</span>
                  )}
                </div>
              ) : null}
              {execMut.data.skipped?.length ? (
                <div className="mt-1 text-xs">
                  <strong className="text-warn">Skipped:</strong>{" "}
                  {execMut.data.skipped.map((l, i) =>
                    <span key={i} className="mr-2">{l.direction} {l.asset} — {l.reason}</span>
                  )}
                </div>
              ) : null}
              {execMut.data.errors?.length ? (
                <div className="mt-1 text-xs">
                  <strong className="text-bad">Errors:</strong>{" "}
                  {execMut.data.errors.map((l, i) =>
                    <span key={i} className="mr-2">{l.direction} {l.asset} — {l.reason}</span>
                  )}
                </div>
              ) : null}
            </Alert>
          ) : null}

          {dryRun.data ? (
            <div className="mt-3 space-y-3">
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <Stat label="Reason" value={dryRun.data.reason} />
                <Stat label="Computed" value={fmtTime(dryRun.data.computed_at) || "—"} />
                <Stat
                  label="Max drift"
                  value={`${(dryRun.data.max_drift_pct * 100).toFixed(2)}%`}
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Rebalance plan {dryRun.data.rebalance_triggered ? "" : "— no action needed"}</CardTitle>
                </CardHeader>
                <CardContent>
                  {dryRun.data.legs.length === 0 ? (
                    <p className="text-sm text-muted">
                      Drift below threshold for every asset. No legs queued.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-xs text-muted">
                        <tr>
                          <th className="text-left py-1">Asset</th>
                          <th className="text-right">Current</th>
                          <th className="text-right">Target</th>
                          <th className="text-right">Drift</th>
                          <th className="text-right">Direction</th>
                          <th className="text-right">Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dryRun.data.legs.map((leg) => (
                          <tr key={leg.asset} className="border-t border-border">
                            <td className="py-1">{leg.asset}</td>
                            <td className="text-right tabular">{(leg.current_weight * 100).toFixed(1)}%</td>
                            <td className="text-right tabular">{(leg.target_weight * 100).toFixed(1)}%</td>
                            <td className="text-right tabular">
                              <PnLPill value={leg.drift * 100} />%
                            </td>
                            <td className="text-right">
                              <span className={leg.direction === "BUY" ? "text-good" : "text-bad"}>{leg.direction}</span>
                            </td>
                            <td className="text-right tabular">{fmtUsd(leg.size_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Latest persisted target weights</span>
            <StalenessBadge ts={latest?.ts} warnHours={6} staleHours={24} veryStaleHours={72} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {weightsQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : !latest ? (
            <p className="text-sm text-muted">
              No portfolio_weights rows yet. Click <em>Run dry-run</em> above to compute.
            </p>
          ) : (
            <div>
              <div className="text-xs text-muted mb-2">
                {fmtTime(latest.ts)} · reason: <code>{latest.reason}</code>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-muted">
                  <tr>
                    <th className="text-left py-1">Asset</th>
                    <th className="text-right">Weight</th>
                    <th className="text-right">Visualized</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.weights.map((w) => (
                    <tr key={w.asset} className="border-t border-border">
                      <td className="py-1">{w.asset}</td>
                      <td className="text-right tabular">{(w.weight * 100).toFixed(2)}%</td>
                      <td>
                        <div className="h-2 rounded bg-panel-2 ml-auto" style={{ width: `${Math.max(2, w.weight * 100 * 2)}%`, maxWidth: "100%" }}>
                          <div className="h-full rounded bg-accent" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">About this tab</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted space-y-2">
          <p>
            <strong>Honesty check.</strong> MPT on 20 days of crypto daily data is noisy.
            Ledoit-Wolf shrinkage and an L2 regularizer fight corner solutions; assets are
            clipped to <code>mpt_max_per_asset_weight</code>. With under 4 assets the runner
            falls back to equal-weight rather than producing unstable corners.
          </p>
          <p>
            <strong>Threshold rebalance.</strong> Plans only fire when an asset's drift
            exceeds <code>mpt_rebalance_threshold_pct</code>. Each plan is capped at{" "}
            <code>mpt_max_rebalance_trades_per_run</code> legs; remaining drift is queued
            for the next check.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
